import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { rootUrl } from '../config/config';

/**
 * Job Card Stages Context
 * Provides centralized access to job card stages data from backend.
 * 
 * Stage Object Structure:
 * - id: number - Unique stage identifier
 * - code: string - Stage code (e.g., 'OPEN', 'CANCELLED')
 * - name: string - Display name
 * - color: string - Hex color code for UI
 * - sort_order: number - Display order
 * - is_active: boolean - Whether stage is active
 * - invoice: boolean - Whether stage requires invoice
 * - locked: boolean - Whether stage prevents further changes
 * - cancel: boolean - Whether stage represents cancellation
 */
const JobCardStagesContext = createContext();

export const JobCardStagesProvider = ({ children }) => {
    const [stages, setStages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        fetchStages();
    }, []);

    const fetchStages = async () => {
        try {
            setLoading(true);
            setError(null);

            // Get auth token
            const token = localStorage.getItem('authToken');
            const headers = token ? { Authorization: `Bearer ${token}` } : {};

            const response = await axios.get(`${rootUrl}/api/system/job-card-stages/active`, { headers });

            if (response.data.success) {
                setStages(response.data.data);
            } else {
                setStages([]);
                setError('Failed to fetch job card stages');
            }
        } catch (err) {
            console.error('Error fetching job card stages:', err);
            setError(err.message);
            setStages([]);
        } finally {
            setLoading(false);
        }
    };

    const refreshStages = async () => {
        await fetchStages();
    };

    return (
        <JobCardStagesContext.Provider value={{ stages, loading, error, refreshStages }}>
            {children}
        </JobCardStagesContext.Provider>
    );
};

export const useJobCardStages = () => {
    const context = useContext(JobCardStagesContext);
    if (!context) {
        throw new Error('useJobCardStages must be used within a JobCardStagesProvider');
    }
    return context;
};
